<template>
  <div
    ref="scrollable"
    :class="b({ trackVisible, invisible })"
  >
    <slot></slot>
  </div>
</template>

<script>
  import SimpleBar from 'simplebar';

  /**
   * Renders a scrollable container.
   *
   * https://grsmto.github.io/simplebar/
   */
  export default {
    name: 'e-scrollable',

    // components: {},
    // mixins: [],

    props: {
      /**
       * Determines if the track should be visible.
       */
      trackVisible: {
        type: Boolean,
        default: false,
      },

      /**
       * Changes the click on track speed.
       */
      clickOnTrackSpeed: {
        type: Number,
        default: SimpleBar.defaultOptions.clickOnTrackSpeed,
      },

      /**
       * Use to set all scrollbars to be invisible.
       */
      invisible: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['scroll'],

    data() {
      return {
        /**
         * @type {Object} - The SimpleBar Instance.
         */
        simpleBar: null,
      };
    },

    // computed: {},
    // mergedConfig() {},
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      this.$nextTick(() => {
        // Fixes wrong track height calculations
        this.simpleBar = new SimpleBar(this.$el, {
          autoHide: false,
          clickOnTrackSpeed: this.clickOnTrackSpeed,
        });

        this.simpleBar.getScrollElement()?.addEventListener('scroll', this.onScroll);
      });
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      this.simpleBar?.getScrollElement()?.removeEventListener('scroll', this.onScroll);
    },
    // unmounted() {},

    methods: {
      /**
       * Re-emit the contained elements scroll event.
       *
       * @param {Event} event - Passed through scroll event.
       */
      onScroll(event) {
        const scrollElement = this.simpleBar.getScrollElement();

        const params = {
          target: event.target,
          progress: {
            scrollLeft: scrollElement.scrollLeft,
            scrollWidth: scrollElement.scrollWidth,
            clientWidth: scrollElement.clientWidth,
            percentage: Math.round(
              (scrollElement.scrollLeft / (scrollElement.scrollWidth - scrollElement.clientWidth)) * 100,
            ),
          },
        };

        this.$emit('scroll', params);
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:math';
  @use '@/setup/scss/variables';
  // @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  // @use '@/setup/scss/extends' as *;

  .e-scrollable {
    $track-size: 16px;
    $track-pseudo-size: 1px;
    $scrollbar-size: 4px;
    $scrollbar-size--enlarged: 7px;

    height: 100%;
    padding-right: $track-size;
    overflow: auto;

    &--invisible {
      outline: none;

      &:focus {
        outline: none;
      }

      .simplebar-content {
        padding: 0 !important; // stylelint-disable-line declaration-no-important
      }

      .simplebar-scrollbar.simplebar-visible {
        display: none !important; // stylelint-disable-line declaration-no-important
      }
    }

    .simplebar-content-wrapper::-webkit-scrollbar {
      display: none;
    }

    .simplebar-scrollbar {
      cursor: pointer;
    }

    [class].simplebar-scrollbar::before {
      opacity: 1;
      border-radius: 1px;
      background-color: functions.get-theme-var('e-scrollable-thumb-color');
    }

    .simplebar-track::before {
      position: absolute;
      display: none;
      content: '';
      background-color: variables.$color-grayscale--900;
    }

    .simplebar-track.simplebar-vertical {
      right: 0;
      width: $track-size;

      &::before {
        top: calc(#{$track-size} / 2);
        right: calc(calc(#{$track-size} - #{$track-pseudo-size}) / 2);
        bottom: calc(#{$track-size} / 2);
        left: auto;
        width: $track-pseudo-size;
      }

      .simplebar-scrollbar::before {
        top: calc(#{$track-size} / 2);
        right: calc(calc(#{$track-size} - #{$scrollbar-size}) / 2);
        bottom: calc(#{$track-size} / 2);
        left: auto;
        width: $scrollbar-size;
      }
    }

    .simplebar-track.simplebar-horizontal {
      bottom: 0;
      height: $track-size;

      &::before {
        top: auto;
        right: calc(#{$track-size} / 2);
        bottom: calc(calc(#{$track-size} - #{$track-pseudo-size}) / 2);
        left: calc(#{$track-size} / 2);
        height: $track-pseudo-size;
      }

      .simplebar-scrollbar {
        top: auto;
        bottom: calc(calc(#{$track-size} - #{$scrollbar-size}) / 2);
        height: $scrollbar-size;
        opacity: 1;

        &::before {
          right: calc(#{$track-size} / 2);
          left: calc(#{$track-size} / 2);
        }
      }
    }

    &.simplebar-dragging,
    .simplebar-hover {
      [class].simplebar-scrollbar::before {
        border-radius: 3px;
      }

      .simplebar-vertical [class].simplebar-scrollbar::before,
      &.simplebar-vertical [class].simplebar-scrollbar::before {
        right: calc(calc(#{$track-size} - #{$scrollbar-size--enlarged}) / 2);
        width: $scrollbar-size--enlarged;
        transition: width variables.$transition-duration--200 ease-in-out;
      }

      .simplebar-horizontal [class].simplebar-scrollbar,
      &.simplebar-horizontal [class].simplebar-scrollbar {
        bottom: calc(calc(#{$track-size} - #{$scrollbar-size--enlarged}) / 2);
        height: $scrollbar-size--enlarged;
        transition: width variables.$transition-duration--200 ease-in-out;
      }
    }

    &--track-visible {
      .simplebar-track::before {
        display: block;
      }
    }
  }
</style>
